import * as React from 'react';
import { antGrey, appHomeCardsWidgetTitleStyle } from '@/components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import {
    AppPlatform,
    AppStoreType,
    PhasedRelease,
    ReleaseStep,
    ReleaseStepBlockingConfig,
    ReleaseStepStatus,
    ReleaseStepType,
    ReleaseType,
    ReleaseTypesForNonBlockingSteps,
} from '@/types';
import { groupBy, keys, sortBy } from 'lodash';
import { StatusNodeDot } from '@/components/StatusNode';
import {
    getReleaseTypeForNonBlockingSteps,
    isHalfFilledStepForReleaseStep,
    isInnerFilledStepForReleaseStep,
    isNonBlockingStepForReleaseType,
    statusColorForReleaseStepStatus,
} from '@/helpers';
import Popover from 'antd/lib/popover';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import Alert from 'antd/lib/alert/Alert';

const alertTypeForReleaseStepStatus = (status: ReleaseStepStatus) => {
    switch (status) {
        case ReleaseStepStatus.Ready:
            return 'success';
        case ReleaseStepStatus.Pending:
        case ReleaseStepStatus.PendingChecklistOnly:
            return 'warning';
        case ReleaseStepStatus.Blocked:
            return 'error';
        case ReleaseStepStatus.Inactive:
            return 'info';
    }
};
export interface ReleaseStepsWidgetProps {
    steps: ReleaseStep[];
    appStoreType?: AppStoreType;
    phasedRelease?: PhasedRelease;
    platform: AppPlatform;
    shouldHideNonBlockingSteps: boolean;
    nonBlockingStepsConfigMap: Record<
        ReleaseTypesForNonBlockingSteps,
        Record<ReleaseStepType, ReleaseStepBlockingConfig>
    >;
    releaseType: ReleaseType;
    isWorkflowsCIStepEnabled: boolean;
}

export const ReleaseStepsWidget: React.FC<ReleaseStepsWidgetProps> = ({
    steps,
    appStoreType,
    phasedRelease,
    platform,
    shouldHideNonBlockingSteps,
    nonBlockingStepsConfigMap,
    releaseType,
    isWorkflowsCIStepEnabled,
}) => {
    let filteredSteps = steps;
    if (shouldHideNonBlockingSteps) {
        filteredSteps = steps.filter(
            (step) =>
                !isNonBlockingStepForReleaseType(
                    nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
                    step.type,
                ),
        );
    }
    const stepGroups = groupBy(filteredSteps, (step) => step.groupIndex);
    const groupKeys = keys(stepGroups);
    const sortedKeys = sortBy(groupKeys);

    const allStepsCount = steps.length;
    const normalWidgetWidth = 244;
    const nodeSize = 12;

    const gap = (normalWidgetWidth - allStepsCount * nodeSize) / allStepsCount;

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            <Text style={{ ...appHomeCardsWidgetTitleStyle }}>Release step status</Text>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                {sortedKeys.map((groupKey) => {
                    const stepsInGroup = stepGroups[groupKey];
                    const sortedSteps = sortBy(stepsInGroup, (step) => step.orderIndex);

                    return (
                        <div
                            key={groupKey}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap,
                                position: 'relative',
                            }}
                        >
                            {sortedSteps.map((step, stepIndex) => {
                                let outerBorderColor = antGrey.grey8;
                                let itemColor = statusColorForReleaseStepStatus(step.status);
                                let innerFilledStep = isInnerFilledStepForReleaseStep(step);
                                const halfFilledStep = !!(
                                    appStoreType &&
                                    phasedRelease &&
                                    isHalfFilledStepForReleaseStep(step, appStoreType, phasedRelease)
                                );

                                const isNonBlocking = isNonBlockingStepForReleaseType(
                                    nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(releaseType)],
                                    step.type,
                                );

                                if (isNonBlocking) {
                                    itemColor = antGrey.grey6;
                                    outerBorderColor = antGrey.grey6;
                                    innerFilledStep = true;
                                }

                                return (
                                    <div
                                        key={stepIndex}
                                        style={{
                                            position: 'relative',
                                            zIndex: 1,
                                        }}
                                    >
                                        <Popover
                                            content={
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 8,
                                                        width: 260,
                                                        padding: 4,
                                                    }}
                                                >
                                                    <Text
                                                        style={{ color: antGrey.grey8, fontSize: 14, fontWeight: 600 }}
                                                    >
                                                        {displayTextForReleaseStepType(
                                                            step.type,
                                                            platform,
                                                            appStoreType,
                                                            isWorkflowsCIStepEnabled,
                                                        )}
                                                    </Text>
                                                    <Alert
                                                        className="release-step-status-alert"
                                                        message={step.statusReasonString}
                                                        showIcon
                                                        type={alertTypeForReleaseStepStatus(step.status)}
                                                    />
                                                </div>
                                            }
                                        >
                                            <div>
                                                <StatusNodeDot
                                                    statusColor={itemColor}
                                                    halfFilled={halfFilledStep}
                                                    innerFilled={innerFilledStep}
                                                    outerBorderColor={outerBorderColor}
                                                    size={nodeSize}
                                                    outerBorderWidth={1}
                                                />
                                            </div>
                                        </Popover>
                                    </div>
                                );
                            })}
                            <div
                                style={{
                                    position: 'absolute',
                                    height: 1,
                                    width: '100%',
                                    left: 0,
                                    zIndex: 0,
                                    top: 'calc(50% - 0.5px)',
                                    backgroundColor: antGrey.grey6,
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
